import { getNodeContents, isElement, isElementWithTagName, isText } from '../../../helpers/dom/node';
import { filterIterable, someInIterable } from '../../../helpers/iterable';
import { isOnlyCollapsibleWhitespace } from '../../../helpers/whitespace';
import { ContentEditableEditorPlugin } from '../../base/plugins/contentEditableEditorPlugin';

export const createContentEditableMarkdownBeforePlugin = (): ContentEditableEditorPlugin => ({
  processTree: element => {
    processPreformattedTextElements(element);
  },
});

/** Ensures a <pre> element representing a code block is valid. */
function processPreformattedTextElements(element: Element) {
  const document = element.ownerDocument;
  if (!document) {
    return;
  }

  element.querySelectorAll('pre').forEach(preElement => {
    // Make sure it contains a <code> element as its only child.
    const codeElement = findOrCreatePreformattedTextCodeElement(document, preElement);
    processPreformattedTextCodeElement(document, codeElement);
  });
}

/** Returns the <code> element inside the given code block, wrapping its children in a new one if necessary. */
function findOrCreatePreformattedTextCodeElement(
  document: Document,
  preElement: HTMLPreElement,
): HTMLElement {
  const onlySignificantChildNode = findOnlySignificantChildNode(preElement);
  if (onlySignificantChildNode && isElementWithTagName(onlySignificantChildNode, 'code')) {
    return onlySignificantChildNode;
  }

  const codeElement = document.createElement('code');
  const contents = getNodeContents(preElement);
  codeElement.appendChild(contents);
  preElement.appendChild(codeElement);
  return codeElement;
}

/** Returns the only non-whitespace text node or element, if there is such a child. */
function findOnlySignificantChildNode(node: Node): Node | null {
  // Collect non-empty text nodes and elements.
  const significantChildNodes = Array.from(
    filterIterable(
      node.childNodes,
      childNode =>
        (isText(childNode) && !isOnlyCollapsibleWhitespace(childNode.data)) || isElement(childNode),
    ),
  );

  return significantChildNodes.length === 1 ? significantChildNodes[0] : null;
}

/** Ensures the <code> element in a code block is valid. */
function processPreformattedTextCodeElement(document: Document, codeElement: HTMLElement): void {
  // Require there to be a non-empty text node or element.
  if (
    someInIterable(
      codeElement.childNodes,
      childNode => (isText(childNode) && childNode.length > 0) || isElement(childNode),
    )
  ) {
    return;
  }

  // Markdown renders an empty code block with a newline, but a <br> works better for cursor placement.
  const fillerElement = document.createElement('br');
  codeElement.appendChild(fillerElement);
}
